import { useQuery } from "@tanstack/react-query";

import { useCurrentCompanyId } from "@/hooks/useCurrentCompanyId";
import { useCurrentJobId } from "@/hooks/useCurrentJobId";
import { guacHttpClient } from "@/lib/http";
import { HttpResponse, SallyCandidates } from "@/types";

const sallyCandidatesQueryKey = (companyId: string, jobId: string) =>
  ["sally-candidates", companyId, jobId] as const;

/**
 * Every candidate Sally owns on this job, with per-state counts.
 *
 * Deliberately not a search: guac reads the job's sourced candidacies directly,
 * so the set is complete regardless of query, radius, or search's exclusion
 * rules (which hide rejected sourced candidacies by default — a large part of
 * this population). The ids then become the corpus for nova's id-restricted
 * search, which is what lets you search WITHIN them.
 */
export function useGetSallyCandidates({ enabled }: { enabled: boolean }) {
  const currentCompanyId = useCurrentCompanyId();
  const currentJobId = useCurrentJobId();

  return useQuery({
    enabled,
    queryKey: sallyCandidatesQueryKey(currentCompanyId, currentJobId),
    queryFn: async () => {
      const path = `companies/${currentCompanyId}/jobs/${currentJobId}/sourced-candidacies/sally-candidates`;
      const res = await guacHttpClient.get<
        SallyCandidates,
        HttpResponse<SallyCandidates>
      >(path);
      return res.data;
    },
  });
}
